<script lang="ts">
  import BaseStep from "./BaseStep.svelte";
  import SharedHeading from "../SharedHeading.svelte";
  import { getCopy } from "../../lib/stores/copy";
  import Button from "../Button.svelte";
  import Input from "../Inputs/Input.svelte";
  import Select from "../Inputs/Select.svelte";
  import {
    validateDate,
    getMonthsOptions,
    calculateAgeDetails,
    getInputLabels,
    continueHandler
  } from "../../lib/helpers/steps/checkAge";
  import Typography from "../Typography.svelte";
  import { TypographyType } from "../../lib/constants/typographyMap";
  import { sessionStore } from "../../lib/stores/session";
  import CustomHtml from "../CustomHTML.svelte";

  export let currentRoute;

  $: copy = $getCopy("ageCheck") as Record<string, string>;
  $: generalCopy = $getCopy("general", { basePath: "" }) as Record<string, string>;

  $: monthsOptions = getMonthsOptions($sessionStore.language);
  $: inputLabels = getInputLabels($sessionStore.language);

  let day = "";
  let year = "";
  let month = "";

  let isSaving = false;
  let apiErrorOccurred = false;

  let isValidDate: boolean;
  let hasError: boolean;

  const validateFields = () => {
    apiErrorOccurred = false;
    isValidDate = validateDate({ month, day, year });
    hasError = !!(year?.toString().length > 3 && day && month && !isValidDate);
  };

  const handleContinue = async () => {
    try {
      isSaving = true;
      apiErrorOccurred = false;
      const { age, dob } = calculateAgeDetails({ month, day, year });
      await continueHandler({
        age: age,
        limit: copy?.ageLimit,
        dob: dob
      });
    } catch (error) {
      apiErrorOccurred = true;
    } finally {
      isSaving = false;
    }
  };
</script>

<BaseStep {currentRoute}>
  <svelte:fragment slot="content">
    <SharedHeading headerCopy={copy.headerCopy} subHeaderCopy={copy.subHeaderCopy} />

    <div class="flex flex-col lg:flex-row mt-4 lg:mt-8 gap-4">
      <div class="w-full lg:w-fit min-w-48">
        <Select
          bind:value={month}
          isDisabled={isSaving}
          optionList={monthsOptions}
          placeholder={inputLabels.month}
          onChange={validateFields} />
      </div>
      <div class="w-full lg:w-fit min-w-32">
        <Input
          type="number"
          inputProps={{ min: 1, max: 31 }}
          bind:value={day}
          isDisabled={isSaving}
          placeholder={inputLabels.day}
          onChange={validateFields} />
      </div>
      <div class="w-full lg:w-fit min-w-32">
        <Input
          type="number"
          inputProps={{ max: new Date().getFullYear() }}
          bind:value={year}
          isDisabled={isSaving}
          placeholder={inputLabels.year}
          onChange={validateFields} />
      </div>
    </div>

    <div class="mt-4 lg:mt-10 flex flex-col gap-4">
      {#if apiErrorOccurred || hasError}
        <div class="lg:-mt-6 h-2">
          <Typography
            mobile={TypographyType.Body2Regular}
            desktop={TypographyType.Body2Medium}
            --font-color="var(--color-red-50)">
            <span aria-label="error-message">{hasError ? copy?.invalidDOBAlert : generalCopy?.fatalErrorCopy}</span>
          </Typography>
        </div>
      {/if}

      <Button
        class="w-full lg:w-fit"
        primary
        disabled={!isValidDate || isSaving}
        isBusy={isSaving}
        on:click={handleContinue}>
        {copy.continueButtonCopy}
      </Button>
    </div>

    <CustomHtml customHtml={copy.customHtml} />
  </svelte:fragment>
</BaseStep>
